import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import Input from "../../components/form/input/InputField";
import ListboxComponent from "../../components/common/ListboxComponent";
import { useNavigate } from "react-router-dom";
import { styles } from "../../styles/pages/Lead/LeadAddStyles";
 
// Interfaces
interface Stage {
  id: number;
  stage_name: string;
}
 
interface User {
  id: number;
  name: string;
}
 
interface Errors {
  name?: string;
  lead_stage?: string;
  address?: string;
  email?: string;
  contact?: string;
  lead_source?: string;
  lead_status?: string;
  opportunity_amount?: string;
}
 
interface FormData {
  lead_stage: string;
  name: string;
  address: string;
  email: string;
  contact: string;
  lead_source: string;
  lead_status: string;
  company_name: string;
  company_website: string;
  opportunity_amount: string;
  description: string;
  referred_by: string;
  value: string;
}
 
const AddLead = () => {
  const navigate = useNavigate();
  const [stages, setStages] = useState<Stage[]>([]);
  const [, setUsers] = useState<User[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<{ id: number; status_name: string }[]>([]);
  const [leadSources, setLeadSources] = useState<{ id: number; source_name: string }[]>([]);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [socialProfiles, setSocialProfiles] = useState<{ network: string; url: string }[]>([]);
  const [fetchingProfiles, setFetchingProfiles] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string>("");
 
  const token = sessionStorage.getItem("access_token");
 
  // Form State
  const [formData, setFormData] = useState<FormData>({
    lead_stage: "",
    name: "",
    address: "",
    email: "",
    contact: "",
    lead_source: "",
    lead_status: "",
    company_name: "",
    company_website: "",
    opportunity_amount: "",
    description: "",
    referred_by: "",
    value: "",
  });
 
  // Errors State
  const [errors, setErrors] = useState<Errors>({});
 
  // Handle Input Change
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({
      ...prev,
      [field]:
        field === "email"
          ? !isValidEmail(value)
            ? "Please enter a valid email address."
            : undefined
          : field === "contact"
            ? !validContact(value)
              ? "Please enter a valid 10-digit contact number."
              : undefined
            : value.trim() === ""
              ? `The ${field.replace(/_/g, " ")} field is required`
              : undefined,
    }));
  };
 
  // Handle Contact Number
  const handleContactNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, contact: formattedValue }));
    setErrors((prev) => ({
      ...prev,
      contact: !formattedValue
        ? "Mobile number field is required"
        : !validContact(formattedValue)
          ? "Please enter a valid 10-digit contact number."
          : undefined,
    }));
  };
 
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
 
  const validContact = (contact: string): boolean => {
    return /^\d{10}$/.test(contact);
  };
 
  // Validate Form
const validate = (): Errors => {
  const newErrors: Errors = {};
 
  if (!formData.name.trim()) newErrors.name = "The name field is required";
  if (!formData.lead_stage.trim()) newErrors.lead_stage = "The lead stage field is required";
  if (!formData.address.trim()) newErrors.address = "The address field is required";
  if (!formData.email.trim()) newErrors.email = "The email field is required";
  if (!formData.contact.trim()) newErrors.contact = "The contact field is required";
  if (!formData.lead_source.trim()) newErrors.lead_source = "The lead source field is required";
  if (!formData.lead_status.trim()) newErrors.lead_status = "The lead status field is required";
  if (!formData.opportunity_amount.trim())
    newErrors.opportunity_amount = "The opportunity amount field is required";
  return newErrors;
};
 
 
  // Handle Form Submission
  // const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  //   e.preventDefault();
  //   setSaving(true);
  //   setErrors({});
 
  //   const validationErrors = validate();
  //   if (Object.keys(validationErrors).length > 0) {
  //     setErrors(validationErrors);
  //     setSaving(false);
  //     return;
  //   }
 
  //   try {
  //     const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leads/store`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify(formData),
  //     });
 
  //     const data = await response.json();
  //     if (data.status === 200) {
  //       setSuccessModalOpen(true);
  //       setTimeout(() => {
  //         setSuccessModalOpen(false);
  //         setFormData({
  //           lead_stage: "",
  //           name: "",
  //           address: "",
  //           email: "",
  //           contact: "",
  //           lead_source: "",
  //           lead_status: "",
  //           company_name: "",
  //           company_website: "",
  //           opportunity_amount: "",
  //           description: "",
  //           referred_by: "",
  //           value: "",
  //         });
  //         navigate("/lead-list");
  //       }, 2000);
  //     } else {
  //       setErrors({ email: data.message || "Something went wrong." });
  //     }
  //   } catch (error: any) {
  //     setErrors({ email: error.message || "Failed to submit lead. Please try again." });
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  

const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  setSaving(true);
  setErrors({});
  setFileErrors("");

  const validationErrors = validate();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    setSaving(false);
    return;
  }

  const form = new FormData();
  Object.entries(formData).forEach(([key, value]) => {
    form.append(key, value);
  });
  attachments.forEach((file) => form.append("attachments[]", file));

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leads/store`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: form,
    });

    const data = await response.json();
    if (data.status === 200) {
      setSuccessModalOpen(true);
      setTimeout(() => {
        setSuccessModalOpen(false);
        setFormData({
          lead_stage: "",
          name: "",
          address: "",
          email: "",
          contact: "",
          lead_source: "",
          lead_status: "",
          company_name: "",
          company_website: "",
          opportunity_amount: "",
          description: "",
          referred_by: "",
          value: "",
        });
        setAttachments([]);
        navigate("/lead-list");
      }, 2000);
    } else {
      setErrors({ email: data.message || "Something went wrong." });
    }
  } catch (error: any) {
    setErrors({ email: error.message || "Failed to submit lead. Please try again." });
  } finally {
    setSaving(false);
  }
};

 
  // Fetch Data
  useEffect(() => {
    const fetchStages = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stage/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status === 200) setStages(result.data);
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };
    fetchStages();
  }, [token]);
 
  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.status === 200) setUsers(data.data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [token]);
 
  useEffect(() => {
    const fetchLeadStatuses = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/status/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status === 200) setLeadStatuses(result.data);
      } catch (error) {
        console.error("Error fetching lead statuses:", error);
      }
    };
    fetchLeadStatuses();
  }, [token]);
 
  useEffect(() => {
    const fetchLeadSources = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/source/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.status === 200) setLeadSources(data.data);
      } catch (error) {
        console.error("Error fetching lead sources:", error);
      }
    };
    fetchLeadSources();
  }, [token]);
 
useEffect(() => {
  const fetchSocialProfiles = async () => {
    const email = formData.email?.trim();
 
    if (!email || !isValidEmail(email)) {
      setSocialProfiles([]);
      return;
    }
 
    setFetchingProfiles(true);
 
    try {
      const apiKey = "bdafe4a8d6119c3c94ca353ba0247bd435ee2156a1e9a2d4d54a702accd4abd1"; // ← unsafe, frontend only
 
      const apiUrl = `https://api.peopledatalabs.com/v5/person/enrich?email=${encodeURIComponent(email)}&api_key=${apiKey}`;
      const response = await fetch(apiUrl);
      const result = await response.json();
 
      console.log(" PDL API response:", result);
 
      if (result?.data?.profiles?.length > 0) {
        setSocialProfiles(result.data.profiles);
      } else {
        setSocialProfiles([]);
        console.warn("No social profiles found for:", email);
      }
    } catch (error) {
      console.error("Error fetching social profiles:", error);
      setSocialProfiles([]);
    } finally {
      setFetchingProfiles(false);
    }
  };
 
  fetchSocialProfiles();
}, [formData.email]);
 
 const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const files = Array.from(e.target.files || []);
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/png",
    "image/jpg",
  ];

  const invalidFiles = files.filter((file) => !allowedTypes.includes(file.type));

  if (invalidFiles.length > 0) {
    setFileErrors("Only PDF, DOC, DOCX, JPG, JPEG, and PNG files are allowed.");
    return;
  }

  setFileErrors("");
  setAttachments(files);
};

 
 
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Lead</h2>
      </div>
 
      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formFields}>
            {/* Name */}
            <div>
              <label className={styles.label}>
                Name <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`${styles.inputField} ${errors.name ? styles.inputError : styles.inputDefault}`}
              />
              {errors.name && <span className={styles.errorText}>{errors.name}</span>}
            </div>
 
            {/* Email */}
            <div>
              <label className={styles.label}>
                Email <span className={styles.required}>*</span>
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`${styles.inputField} ${errors.email ? styles.inputError : styles.inputDefault}`}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              {/* Social Profiles from Email */}
{fetchingProfiles ? (
  <p className="text-sm text-gray-500">Checking for social profiles...</p>
) : socialProfiles.length > 0 ? (
  <div className="mt-2">
    <label className={styles.label}>Social Media Profiles</label>
    <ul className="list-disc pl-5 text-sm text-blue-600">
      {socialProfiles.map((profile, idx) => (
        <li key={idx}>
          <a href={profile.url} target="_blank" rel="noopener noreferrer" className="underline">
            {profile.network} - {profile.url}
          </a>
        </li>
      ))}
    </ul>
  </div>
) : formData.email && isValidEmail(formData.email) ? (
  <p className="text-sm text-gray-500">No social profiles found for this email.</p>
) : null}
 
            </div>
 
            {/* Contact */}
            <div>
              <label className={styles.label}>
                Mobile Number <span className={styles.required}>*</span>
              </label>
              <Input
                type="tel"
                value={formData.contact}
                onChange={handleContactNumber}
                className={`${styles.inputField} ${errors.contact ? styles.inputError : styles.inputDefault}`}
              />
              {errors.contact && <span className={styles.errorText}>{errors.contact}</span>}
            </div>
 
            {/* Lead Source */}
            <div>
              <ListboxComponent
                value={leadSources.find((source) => source.id === Number(formData.lead_source)) ?? null}
                onChange={(source) => handleInputChange("lead_source", source ? String(source.id) : "")}
                items={leadSources}
                label="Lead Source"
                displayKey="source_name"
                error={errors.lead_source}
                required
              />
            </div>
 
            {/* Lead Stage */}
            <div>
              <ListboxComponent
                value={stages.find((stage) => stage.id === Number(formData.lead_stage)) ?? null}
                onChange={(stage) => handleInputChange("lead_stage", stage ? String(stage.id) : "")}
                items={stages}
                label="Lead Stage"
                displayKey="stage_name"
                error={errors.lead_stage}
                required
              />
            </div>
           
            {/* Lead Status */}
            <div>
              <ListboxComponent
                value={leadStatuses.find((status) => status.id === Number(formData.lead_status)) ?? null}
                onChange={(status) => handleInputChange("lead_status", status ? String(status.id) : "")}
                items={leadStatuses}
                label="Lead Status"
                displayKey="status_name"
                error={errors.lead_status}
                required
              />
            </div>
 
            {/* Company Name */}
            <div>
              <label className={styles.label}>Company Name</label>
              <Input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleInputChange("company_name", e.target.value)}
                className={`${styles.inputField} ${styles.inputDefault}`}
              />
            </div>
 
            {/* Company Website */}
            <div>
              <label className={styles.label}>Company Website</label>
              <Input
                type="text"
                value={formData.company_website}
                onChange={(e) => handleInputChange("company_website", e.target.value)}
                className={`${styles.inputField} ${styles.inputDefault}`}
              />
            </div>
 
            {/* Address */}
            <div>
              <label className={styles.label}>
                Address <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                className={`${styles.inputField} ${errors.address ? styles.inputError : styles.inputDefault}`}
              />
              {errors.address && <span className={styles.errorText}>{errors.address}</span>}
            </div>
 
            {/* Opportunity Amount */}
            <div>
              <label className={styles.label}>
                Opportunity Amount <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.opportunity_amount}
                onChange={(e) => handleInputChange("opportunity_amount", e.target.value)}
                className={`${styles.inputField} ${errors.opportunity_amount ? styles.inputError : styles.inputDefault}`}
                numericOnly={true}
              />
              {errors.opportunity_amount && (
                <span className={styles.errorText}>{errors.opportunity_amount}</span>
              )}
            </div>
 
            {/* Referred By */}
            <div>
              <label className={styles.label}>Referred By</label>
              <Input
                type="text"
                value={formData.referred_by}
                onChange={(e) => handleInputChange("referred_by", e.target.value)}
                className={`${styles.inputField} ${styles.inputDefault}`}
              />
            </div>
 
            {/* Value */}
            <div>
              <label className={styles.label}>Value</label>
              <Input
                type="text"
                value={formData.value}
                onChange={(e) => handleInputChange("value", e.target.value)}
                className={`${styles.inputField} ${styles.inputDefault}`}
                numericOnly={true}
              />
            </div>
 
{/* Description & Attachments Row */}
<div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
  {/* Description (2 columns) */}
  <div className="md:col-span-2">
    <label className={styles.label}>Description</label>
    <textarea
      value={formData.description}
      onChange={(e) => handleInputChange("description", e.target.value)}
      className={`${styles.inputField} min-h-[100px]`}
    />
  </div>

  {/* Attachments (1 column) */}
  <div className="md:col-span-1">
    <label className={styles.label}>Attachments (PDF, DOC, Images)</label>
    <input
      type="file"
      multiple
      accept=".pdf,.doc,.docx,image/*"
      onChange={handleFileChange}
      className={`${styles.inputField} ${styles.inputDefault}`}
    />
    {fileErrors && <span className={styles.errorText}>{fileErrors}</span>}

    {attachments.length > 0 && (
      <ul className="mt-2 list-disc pl-5 text-sm text-gray-600">
        {attachments.map((file, idx) => (
          <li key={idx}>{file.name}</li>
        ))}
      </ul>
    )}
  </div>
</div>


          </div>
 
          <div className={styles.buttonsContainer}>
            <button
              className={styles.submitButton}
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Submit"}
            </button>
            <button
              className={styles.cancelButton}
              type="button"
              onClick={() => navigate("/lead-list")}
            >
              Cancel
            </button>
          </div>
 
          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className={styles.modal}
          >
            <button
              onClick={() => setSuccessModalOpen(false)}
              className={styles.modalCloseButton}
            ></button>
            <div className={styles.modalIconContainer}>
              <CheckCircle className="w-6 h-6" />
            </div>
            <h4 className={styles.modalTitle}>Lead Added Successfully</h4>
            <p className={styles.modalMessage}>
              The lead has been added to the system successfully.
            </p>
            <div className={styles.modalButtonContainer}>
              <button
                onClick={() => setSuccessModalOpen(false)}
                className={styles.modalButton}
              >
                OK
              </button>
            </div>
          </Modal>
        </form>
      </div>
    </div>
  );
};
 
export default AddLead;
 
 